import { billingDownloadFile, billingRequest } from "./billing-client";

export interface InvoiceLine {
  id: string;
  lineNumber: number;
  description: string;
  quantity: string;
  unit?: string;
  unitPrice: string;
  taxRate: string;
  discountRate?: string;
  lineTotal: string;
  productId?: string | null;
  product?: { id: string; code: string; name: string; sku: string } | null;
}

export interface InvoiceDto {
  id: string;
  number: string;
  referenceNumber?: string | null;
  status: string;
  currency: string;
  subtotal: string;
  taxTotal: string;
  cgstAmount?: string;
  sgstAmount?: string;
  discountAmount?: string;
  total: string;
  paidAmount: string;
  dueDate: string | null;
  issuedAt: string | null;
  invoiceDate?: string | null;
  paymentMode?: string | null;
  notes?: string | null;
  termsConditions?: string | null;
  billedBy?: unknown;
  billedTo?: unknown;
  bankDetails?: unknown;
  createdAt: string;
  metadata?: unknown;
  customer: {
    id: string;
    code: string;
    displayName: string;
    email: string | null;
    phone?: string | null;
    country?: string | null;
  };
  lines: InvoiceLine[];
}

export interface InvoiceListResponse {
  items: InvoiceDto[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface CreateInvoicePayload {
  customerId: string;
  currency?: string;
  referenceNumber?: string;
  dueDate?: string;
  notes?: string;
  lines: Array<{
    productId?: string;
    description?: string;
    quantity: number;
    unitPrice: number;
    taxRate?: number;
  }>;
}

export const invoicesApi = {
  list: (params?: { page?: number; limit?: number; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.page) q.set("page", String(params.page));
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.status) q.set("status", params.status);
    const query = q.toString();
    return billingRequest<InvoiceListResponse>(`/invoices${query ? `?${query}` : ""}`);
  },
  summary: () => billingRequest<Record<string, string | number>>("/invoices/summary"),
  get: (id: string) => billingRequest<InvoiceDto>(`/invoices/${id}`),
  create: (payload: CreateInvoicePayload) =>
    billingRequest<InvoiceDto>("/invoices", { method: "POST", body: JSON.stringify(payload) }),
  update: (id: string, payload: Partial<CreateInvoicePayload>) =>
    billingRequest<InvoiceDto>(`/invoices/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  remove: (id: string) =>
    billingRequest<{ deleted: boolean }>(`/invoices/${id}`, { method: "DELETE" }),
  issue: (id: string) =>
    billingRequest<InvoiceDto>(`/invoices/${id}/issue`, { method: "POST" }),
  cancel: (id: string) =>
    billingRequest<InvoiceDto>(`/invoices/${id}/cancel`, { method: "POST" }),
  recordPayment: (id: string, amount: number, paymentMode?: string) =>
    billingRequest<InvoiceDto>(`/invoices/${id}/payments`, {
      method: "POST",
      body: JSON.stringify({ amount, paymentMode }),
    }),
  downloadPdf: (id: string) => billingDownloadFile(`/invoices/${id}/pdf`, `invoice-${id}.pdf`),
};
